// src/pages/designer/DesignerLogOrder.jsx — log a sale into the order pipeline
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  getSettings,
  submitDesignerLoggedOrder,
  commissionRateForDesignerProfile,
  updateOrder,
} from "../../lib/db";
import { Btn, Field, Input, Toast, Spinner, SectionLabel, fmt$ } from "../../components/UI";

const emptyForm = () => ({
  orderNumber: "",
  projectId: "",
  clientName: "",
  description: "",
  amount: "",
  attachmentUrl: "",
  attachmentName: "",
});

export default function DesignerLogOrder() {
  const pagePad = "var(--page-pad-x)";
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    getSettings().then(setSettings);
  }, []);

  if (!profile) return <Spinner />;

  const rate =
    settings != null
      ? commissionRateForDesignerProfile(profile, settings)
      : profile.commissionPending === true
        ? null
        : Number(profile.commissionRate) || 8;
  const commissionLocked = rate == null;
  const amtPreview = Number(form.amount);
  const commissionPreview =
    !commissionLocked && Number.isFinite(amtPreview) && amtPreview > 0 ? Math.round((amtPreview * rate) / 100) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const salesOrder = form.orderNumber.trim();
    const pid = form.projectId.trim();
    if (!salesOrder) {
      setToast("Sales order number is required.");
      setTimeout(() => setToast(""), 4000);
      return;
    }
    if (!pid) {
      setToast("Project ID is required.");
      setTimeout(() => setToast(""), 4000);
      return;
    }
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setToast("Enter a valid order amount.");
      setTimeout(() => setToast(""), 4000);
      return;
    }
    if (commissionLocked) {
      setToast("Your commission rate is still pending showroom confirmation. You can log orders once it is set.");
      setTimeout(() => setToast(""), 5000);
      return;
    }

    setSubmitting(true);
    setToast("");
    try {
      const designerName = profile.name || "Designer";
      const orderId = await submitDesignerLoggedOrder({
        orderNumber: salesOrder,
        projectId: pid,
        designerUid: profile.uid,
        designerName,
        clientName: form.clientName.trim(),
        description: form.description.trim(),
        amount: amt,
        commissionRate: rate,
      });

      const link = form.attachmentUrl.trim();
      const linkLabel = form.attachmentName.trim();
      if (link) {
        await updateOrder(orderId, {
          attachmentUrl: link,
          attachmentName: linkLabel || "Attachment link",
        });
      }

      setForm(emptyForm());
      setToast("Order logged. You can track it under My Orders.");
      setTimeout(() => navigate("/portal/orders"), 1200);
    } catch (err) {
      setToast(err.message || "Could not submit order.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!settings) return <Spinner />;

  return (
    <div>
      <Toast msg={toast} onClose={() => setToast("")} />
      <div style={{ padding: `28px ${pagePad} 0` }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300 }}>Log a sale</h1>
        <p style={{ fontSize: 13, color: "var(--mid)", marginTop: 4 }}>
          Log a sale for David Meyer Showroom.
          {commissionLocked
            ? " Your commission rate is pending showroom confirmation; logging will open once your rate is finalized."
            : ` Commission uses your confirmed rate (${rate}%). Orders appear in admin workflow as New.`}
        </p>
      </div>

      <div style={{ padding: `var(--page-pad-y) ${pagePad} 32px`, maxWidth: 560 }}>
        <SectionLabel>Order details</SectionLabel>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Field label="Sales Order Number" half required>
              <Input value={form.orderNumber} onChange={set("orderNumber")} placeholder="DM-2026-050" required />
            </Field>
            <Field label="Project ID" half required>
              <Input value={form.projectId} onChange={set("projectId")} placeholder="PRJ-2044-A" required />
            </Field>
          </div>
          <Field label="Client / Project Name">
            <Input value={form.clientName} onChange={set("clientName")} placeholder="Smith Residence – Master Bath" />
          </Field>
          <Field label="Description">
            <Input value={form.description} onChange={set("description")} placeholder="Fixtures, notes…" />
          </Field>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Field label="Amount ($)" half hint="Exclude taxes and any shipping charges from this amount.">
              <Input type="number" value={form.amount} onChange={set("amount")} placeholder="12400" required min={1} />
            </Field>
            <Field label={commissionLocked ? "Commission" : `Commission (${rate}%)`} half hint={commissionLocked ? "Pending showroom confirmation." : "From your tier; showroom confirms when the order is fulfilled."}>
              <Input type="text" value={commissionLocked ? "Pending" : commissionPreview != null ? fmt$(commissionPreview) : "—"} readOnly disabled style={{ opacity: 0.9 }} />
            </Field>
          </div>
          <Field
            label="Attachment link (optional)"
            hint="Paste a Dropbox, Drive, or showroom URL — Firebase Storage is not used (Spark-friendly)."
          >
            <Input
              type="text"
              value={form.attachmentUrl}
              onChange={set("attachmentUrl")}
              placeholder="https://…"
              disabled={submitting}
            />
          </Field>
          <Field label="Link label (optional)" hint="Shown as the clickable text (e.g. “Proposal PDF”).">
            <Input value={form.attachmentName} onChange={set("attachmentName")} placeholder="Proposal / invoice" disabled={submitting} />
          </Field>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <Btn type="button" onClick={() => navigate("/portal/orders")} disabled={submitting}>
              Cancel
            </Btn>
            <Btn type="submit" variant="gold" disabled={submitting || commissionLocked}>
              {commissionLocked ? "Rate pending" : submitting ? "Saving…" : "Log order"}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
